import {
  ChargeDto,
  CreateChargeCommand,
} from "../../../features/order/order-api";

/**
 * Replaces the rate-based charge of the order with the charge quoted for the
 * newly selected rate.
 *
 * The whole charge is taken from the quote. Patching only `rateId` keeps the
 * previous rate's tariff, custom values (vKgFactor, service description) and
 * price, and the backend then prices the charge with a tariff that belongs to
 * a different rate (TRTEXP-296).
 *
 * Only `chargeStatus` of the replaced charge is preserved: it belongs to the
 * draft, not to the quote. When the order has no rate-based charge yet, the
 * quoted one is appended.
 *
 * Returns the original array when the quote has no charge for `rateId`.
 */
export const applyRateQuoteToCharges = (
  charges: CreateChargeCommand[],
  quoteCharges: ChargeDto[] | null | undefined,
  rateId: number,
): CreateChargeCommand[] => {
  const quotedCharge = quoteCharges?.find((charge) => charge.rateId === rateId);
  if (!quotedCharge) return charges;

  const isReplaced = (charge: CreateChargeCommand) =>
    charge.values?.rateId != null ||
    (!!quotedCharge.accountingItemCode &&
      charge.values?.accountingItemCode === quotedCharge.accountingItemCode);

  const replacedIndex = charges.findIndex(isReplaced);
  const replaced = replacedIndex >= 0 ? charges[replacedIndex] : undefined;

  const quotedCommand: CreateChargeCommand = {
    organizationId: replaced?.organizationId ?? quotedCharge.organizationId,
    values: {
      ...(quotedCharge as CreateChargeCommand["values"]),
      chargeStatus: replaced?.values?.chargeStatus ?? quotedCharge.chargeStatus,
    },
  };

  const result = charges.filter((charge) => !isReplaced(charge));
  result.splice(
    replacedIndex >= 0 ? replacedIndex : result.length,
    0,
    quotedCommand,
  );

  return result;
};
